import React, { FC, Fragment, useState, useCallback, useEffect, useContext } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import moment, { Moment } from 'moment';
import styled, { css } from 'styled-components';

import DownArrowAndCircleIcon from '@ad-scripts/presentation/components/common/icons/stroke/down-arrow-and-circle-stroke';
import PopoverMenu, { usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import { IvrConfiguration } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import { LongTextWithTooltip, TooltipPosition } from 'src/presentation/components/pages/shared/LongTextWithTooltip';
import { OpenerButton, ButtonTextWrapper, ChevronIcon } from '@ad-scripts/presentation/components/common/dropdown_menu';
import { StyledTR, StyledTD } from 'src/presentation/components/pages/shared/Table';
import { Table, TBody } from '@ad-scripts/presentation/components/common/table';
import { VersionManagerContext } from './VersionManager';

type VersionBarProps = {
  'data-aid'?: string;
};

const getDateOrTimeStr = (selectedVersion: IvrConfiguration | undefined) => {
  if (has(selectedVersion, 'createdAt')) {
    const versionDate: Moment | null = moment.utc(get(selectedVersion, 'createdAt')).local();
    const isToday = moment(versionDate).format('DD/MM/YYYY') === moment().format('DD/MM/YYYY');
    return isToday ? versionDate.format('h:mmA') : versionDate.format('MMM D');
  }
  return '';
};

export const VersionBar: FC<VersionBarProps> = ({ 'data-aid': dataAid }) => {
  const { isNew, items, selected } = useContext(VersionManagerContext);

  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const chevronIcon = <ChevronIcon data-isopen={isOpen} disabled={false} />;

  const [publishedVersion, setPublishedVersion] = useState<number>();
  const [archivedVersions, setArchivedVersions] = useState<IvrConfiguration[]>([]);
  const [draftVersions, setDraftVersions] = useState<IvrConfiguration[]>([]);

  useEffect(() => {
    const publishedVersion = get(
      find(items, item => !!item.publishedAt),
      'version'
    ) as number;
    setPublishedVersion(publishedVersion);
    setArchivedVersions(filter(items, item => item.version <= publishedVersion && !!item.publishedAt));
    setDraftVersions(filter(items, item => isNil(publishedVersion) || item.version > publishedVersion));
  }, [items]);

  const listPublishedAndArchived = map(archivedVersions, (item, index: number) => {
    return (
      <VersionRow
        data-aid={`${dataAid}-published-row[${index}]`}
        key={item.version}
        version={item}
        isPublished={item.version === publishedVersion}
        hideActions={item.version === selected}
      />
    );
  });

  const selectedVersion = items.find(item => item.version === selected);
  const isSelectedVersionPublished = selected === publishedVersion;
  const time = getDateOrTimeStr(selectedVersion);

  const name = get(selectedVersion, 'name', get(selectedVersion, 'description', get(selectedVersion, 'id')));

  const selectedValue = (
    <PublishedLabel data-aid={`${dataAid}-published-label`}>
      {isSelectedVersionPublished ? 'Published' : 'Unpublished'}
    </PublishedLabel>
  );

  const versionDescription =
    isSelectedVersionPublished && get(selectedVersion, 'comment') ? (
      <>
        <Splitter data-aid={`${dataAid}-splitter`}> | </Splitter>
        <PublishDescriptionLabel data-aid={`${dataAid}-version-description`}>
          <LongTextWithTooltip
            data-aid={`${dataAid}-version-description-text`}
            position={TooltipPosition.BOTTOM}
            value={get(selectedVersion, 'comment', '')}
          />
        </PublishDescriptionLabel>
      </>
    ) : undefined;

  const handleKeyPress = useCallback(
    event => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <VersionBarWrapper data-aid={dataAid}>
      <OpenerButton
        aria-haspopup="true"
        data-aid={`${dataAid}-opener`}
        onClick={onToggle}
        onKeyDown={handleKeyPress}
        ref={setTargetRef}
      >
        <StyledButtonTextWrapper>
          <NameContainer data-aid={`${dataAid}-ivr-name`}>{name}</NameContainer>
          <Splitter>|</Splitter>
          {isNew ? null : selectedValue}
          <CreateTimeLabel data-aid={`${dataAid}-created-at-label`}>{time}</CreateTimeLabel>
          {versionDescription}
        </StyledButtonTextWrapper>
        {chevronIcon}
      </OpenerButton>

      <PopoverMenu
        data-aid={`${dataAid}-menu`}
        isOpen={isOpen}
        position="bottom"
        targetPosition="center"
        targetRef={targetRef}
      >
        <ScrollableWrapper>
          <StyledTable>
            {draftVersions.length && (
              <TBody data-aid="unpublished-group" role="group">
                <CollapsibleRow
                  key={get(selectedVersion, 'id')}
                  data-aid={`${dataAid}-collapsible-row`}
                  draftVersions={draftVersions}
                />
              </TBody>
            )}
            <TBody data-aid="published-group" role="group">
              {listPublishedAndArchived}
            </TBody>
          </StyledTable>
        </ScrollableWrapper>
      </PopoverMenu>
    </VersionBarWrapper>
  );
};

export const Splitter = styled.span`
  padding: 0 5px;
`;

type VersionRowProps = {
  'data-aid': string;
  version: IvrConfiguration;
  isPublished?: boolean;
  showHistoryLine?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  hideActions?: boolean;
};

const VersionRow: FC<VersionRowProps> = ({
  'data-aid': dataAid,
  version,
  isPublished,
  showHistoryLine,
  isFirst,
  isLast,
  hideActions,
}) => {
  const { changeVersion } = useContext(VersionManagerContext);

  const published = isPublished ? <PublishedLabel>Published</PublishedLabel> : null;
  const time = getDateOrTimeStr(version);

  const onViewVersion = useCallback(() => {
    changeVersion && changeVersion(version.version);
  }, [changeVersion, version]);

  const name = version.comment || version.name || version.description;

  return (
    <Row data-aid={dataAid} role="menuitem">
      <DateTimeColumn>{time}</DateTimeColumn>
      {showHistoryLine && (
        <HistoryLineColumn>
          <HistoryLine isFirst={isFirst} isLast={isLast}></HistoryLine>
        </HistoryLineColumn>
      )}
      <VersionInfoTD colSpan={showHistoryLine ? 1 : 2}>
        <PublishInformationLine>
          {published}
          <PublishInformationLineComment>
            <StyledLongTextWithTooltip
              className="longTextTooltip"
              data-aid="description-text"
              position={TooltipPosition.BOTTOM}
              value={name}
            />
          </PublishInformationLineComment>
        </PublishInformationLine>
      </VersionInfoTD>
      <ActionsColumn>
        {!hideActions && <ActionsPanel data-aid="action-panel" onViewVersion={onViewVersion} />}
      </ActionsColumn>
    </Row>
  );
};

type ActionsPanelProps = {
  'data-aid': string;
  onViewVersion?: Function;
};

const ActionsPanel: FC<ActionsPanelProps> = ({ onViewVersion, 'data-aid': dataAid }) => (
  <ActionsWrapper>
    <Button data-aid={`${dataAid}-view-button`} type={ButtonTypes.SECONDARY} onClick={onViewVersion}>
      View
    </Button>
  </ActionsWrapper>
);

type CollapsibleRowProps = {
  draftVersions: IvrConfiguration[];
  'data-aid'?: string;
};

const CollapsibleRow: FC<CollapsibleRowProps> = ({ draftVersions, 'data-aid': dataAid }) => {
  const { selected } = useContext(VersionManagerContext);

  const [isOpen, setOpen] = useState(true);
  const latestVersion = cloneDeep(draftVersions[0]);

  const showHistoryLine = draftVersions.length > 1;
  const unpublished = map(draftVersions, (item, index: number) => (
    <VersionRow
      data-aid={`${dataAid}[${index}]`}
      hideActions={item.version === selected}
      isFirst={index === 0}
      isLast={index === draftVersions.length - 1}
      key={item.version}
      showHistoryLine={showHistoryLine}
      version={item}
    />
  ));
  const toggle = e => {
    e.stopPropagation();
    setOpen(!isOpen);
  };
  const time = getDateOrTimeStr(latestVersion);
  const showChevronIcon = !isEmpty(draftVersions);

  return (
    <Fragment>
      <Row data-aid={`${dataAid}-header`}>
        <DateTimeColumn>{time}</DateTimeColumn>
        <StyledTD colSpan="3">
          <PublishedLabel>Unpublished</PublishedLabel>
          {showChevronIcon && (
            <UnpublishedChevronIcon data-isopen={isOpen} onClick={toggle} data-aid={`${dataAid}-header-chevron`} />
          )}
        </StyledTD>
      </Row>
      {isOpen && unpublished}
    </Fragment>
  );
};

const StyledLongTextWithTooltip = styled(LongTextWithTooltip)`
  width: 100%;
`;

const VersionInfoTD = styled(StyledTD)`
  max-width: 210px;
`;

const PublishInformationLine = styled.div`
  display: flex;
`;

const PublishInformationLineComment = styled.div`
  align-items: center;
  display: flex;
  overflow: hidden;
  width: 100%;
`;

export const StyledButtonTextWrapper = styled(ButtonTextWrapper)`
  display: flex;
`;

export const ScrollableWrapper = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

export const UnpublishedChevronIcon = styled(DownArrowAndCircleIcon)`
  height: 16px;
  fill: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.gray600)};
  vertical-align: middle;
  width: 16px;
  ${p => p['data-isopen'] && 'transform: rotate(-180deg);'}
`;

const historyLineCircle = css`
  background-color: ${p => p.theme.colors.gray300};
  border-radius: 50%;
  content: '';
  display: inline-block;
  height: 6px;
  left: -3px;
  position: absolute;
  width: 6px;
`;

const historyLineCircleAfter = css`
  &:after {
    ${historyLineCircle}
    bottom: -1px;
  }
`;

const HistoryLine = styled.div`
  border: 1px solid ${p => p.theme.colors.gray300};
  bottom: ${p => (p.isLast ? '45%' : 0)};
  position: absolute;
  top: ${p => (p.isFirst ? '40%' : 0)};
  width: 1px;
  ${p => p.isFirst && historyLineCircleBefore}
  ${p => p.isLast && historyLineCircleAfter};
`;

const HistoryLineColumn = styled(StyledTD)`
  position: relative;
  width: 10px;
`;

const historyLineCircleBefore = css`
  &:before {
    ${historyLineCircle}
    top: -1px;
  }
`;

const Row = styled(StyledTR)`
  line-height: 299%;
`;

export const NameContainer = styled.span`
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;
`;

const PublishedLabel = styled.span`
  font-weight: bold;
  margin-right: 8px;
`;

export const CreateTimeLabel = styled.span`
  color: ${p => p.theme.colors.gray600};
`;

const PublishDescriptionLabel = styled.span`
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ActionsColumn = styled(StyledTD)`
  min-width: 70px;
`;

export const ActionsWrapper = styled.div``;

export const StyledTable = styled(Table)`
  border-radius: 0;
  border: 0;
  min-width: 400px;

  ${ActionsWrapper} {
    visibility: hidden;
  }

  ${StyledTR}:hover {
    ${ActionsWrapper} {
      visibility: visible;
    }
  }
`;

export const DateTimeColumn = styled.td`
  padding-left: 16px;
  font-size: 11px;
  color: ${p => p.theme.colors.gray600};
  width: 55px;
`;

export const VersionBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;
